import React, { useState, useRef, useEffect } from 'react';
import Navbar from './../navbar/navbar';
import styled, { keyframes } from 'styled-components';
import EmojiPicker from 'emoji-picker-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { socket } from './../../socket';

const TextChat = () => {
  const navigate = useNavigate();
  
  const serverHost = process.env.REACT_APP_SERVER_HOST

  const [matchedUser, updateMatchedUser] = useState({
    id: null,
    name: '',
    icon: ''
  });
  const chatsRef = useRef(null);

  const setMatched = (user) => {
    updateMatchedUser({
      id: user.user_id,
      name: 'User',
      icon: user.icon
    });
    setMessages([]);
    console.log('matched with ', user.user_id);
    setMatchVisibility(true);
    addMessage(`Friendly User joined 🤗`, 'system');
  };

  const clearMatch = () => {
    console.log('idle');
    setMatchVisibility(false);
    updateMatchedUser({
      id: null,
      name: '',
      icon: ''
    });
    setMessages([]);
  };

  const setSocketListeners = () => {
    socket.connect();

    socket.on('connect', () => {
      console.log("socket connected");
      socket.emit('connection', { "chat_type": "text", "uid": parseInt(sessionStorage.getItem("user_id"), 10), "username": sessionStorage.getItem("username"), "icon": sessionStorage.getItem("icon") });
    });

    socket.on('disconnect', () => {
      console.log("socket disconnected");
    });

    socket.on('friend-req', (user) => {
      Swal.fire({
        title: 'Friend Request',
        text: `${user.username} wants to be your friend`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Accept',
        cancelButtonText: 'Deny',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const formData = new FormData();
  
          formData.append('user_id', parseInt(sessionStorage.getItem("user_id"), 10));
          formData.append('friend_id', user.user_id);
  
          try {
            const response = await fetch(`${serverHost}/add-friend`, {
              method: 'POST',
              body: formData,
            });
  
            const result = await response.json();
  
            if (response.status === 200) {
              // Handle success, if needed
            } else {
              Swal.fire({
                title: 'Failed to add friend',
                text: result.error,
                icon: 'error',
                confirmButtonText: 'Close'
              });
            }
          } catch (error) {
            console.error('Error adding friend:', error);
          }
        }
      });
    });

    socket.on('match', (user) => {
      setMatched(user);
    });

    socket.on('reported', () => {
      Swal.fire({
        title: 'Warning',
        html: "You have been reported.<br>Multiple reports may result in deletion of your account.",
        icon: 'warning',
        confirmButtonText: 'Close'
      });
    });

    socket.on('unmatch', () => {
      clearMatch();
    });

    socket.on('message', (message) => {
      addMessage(message.trim(), 'user');
    });
  };

  useEffect(() => {
    if (sessionStorage.getItem("user_id") === null) {
      console.log("Not Authenticated");
      navigate('/');
    }

    setSocketListeners();
    
    return () => {
      socket.disconnect();
      socket.removeAllListeners();
    };
  }, []);

  const [isMatched, setMatchVisibility] = useState(false);
  const [textareaValue, setTextareaValue] = useState('');
  const [isEmojiVisible, setEmojiVisibility] = useState(false);

  const addEmoji = ({ emoji }) => {
    setTextareaValue((prevValue) => prevValue + emoji);
  };

  const openCloseEmoji = () => {
    if (isMatched) {
      setEmojiVisibility((prevValue) => !prevValue);
    }
  };

  const navigateHome = () => {
    navigate('/dashboard');
  };

  const [messages, setMessages] = useState([]);

  const sendMessage = () => {
    setEmojiVisibility(() => false);
    const trimmedValue = textareaValue.trim();
    if (trimmedValue !== '') {
      addMessage(trimmedValue, 'mine');
      socket.emit('message', trimmedValue);
      setTextareaValue('');
    }
  };

  const scrollToBottom = () => {
    chatsRef.current.scrollTop = chatsRef.current.scrollHeight;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (text, type) => {
    const newMessage = (
      <Message className={type} key={Date.now()}>
        <p>{text}</p>
      </Message>
    );
    setMessages((prevMessages) => [...prevMessages, newMessage]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.shiftKey && isMatched) {
      e.preventDefault();
      setTextareaValue((prevValue) => prevValue + '\n');
    } else if (e.key === 'Enter' && isMatched) {
      e.preventDefault();
      sendMessage();
    }
  };

  const shuffle = () => {
    socket.disconnect();
    socket.removeAllListeners();
    setMatchVisibility(false);
    updateMatchedUser({
      id: null,
      name: '',
      icon: ''
    });
    setMessages([]);
  
    setTimeout(() => {
      socket.connect();
    }, 5000);
  };

  const report = () => {
    socket.emit('report');
    Swal.fire({
      title: 'User Reported',
      text: 'Thanks for keeping the community healthy 🙇',
      icon: 'success',
      confirmButtonText: 'Close'
    });
  }

  const addFriend = () => {
    socket.emit('send-request');
    Swal.fire({
      title: 'Request Sent',
      text: `Friendly ${matchedUser.name} will be notified of your friend request`,
      icon: 'success',
      confirmButtonText: 'Close'
    });
  }

  return (
    <>
      <Navbar />
      <Container>
        <ChatSection>
          <Head>
            <Info>
              {isMatched && <img src={`avatars/${matchedUser.icon}.png`} alt="user icon" style={{ height: '32px', borderRadius: '50%' }} />}
              {isMatched ? `Friendly ${matchedUser.name}` : <><span>🔎</span>&nbsp;Finding someone for you</>}
              {isMatched && <i className='fa fa-circle' style={{ color: 'green' }} title='coming soon..'/>}
            </Info>
            {isMatched &&
              <UserControls>
                <i className='fa fa-user-plus' style={{ color: 'green', fontSize: '32px', cursor: isMatched ? 'pointer' : 'not-allowed'}} onClick={addFriend} title='Add Friend'/>
                <i className='fa fa-flag' style={{ color: 'red', fontSize: '32px', cursor: isMatched ? 'pointer' : 'not-allowed' }} onClick={report} title='Report User'/>
                <img src="images/shuffle.png" alt="shuffle" style={{ cursor: isMatched ? 'pointer' : 'not-allowed' }} onClick={shuffle} title='Rematch'/>
              </UserControls>
            }
            <img src="images/close.png" alt="close" style={{ cursor: 'pointer' }} onClick={navigateHome} title='Back to dashboard'/>
          </Head>
          <Chats ref={chatsRef}>{messages}</Chats>
          <ChatControls>
            {isEmojiVisible && (
              <EmojiPicker
                onEmojiClick={addEmoji}
                skinTonesDisabled={true}
                style={{ position: 'absolute', top: '-450px', left: '30px' }}
              />
            )}
            <CreatePost>
              {/* <img src="images/smile.png" alt="emoji" onClick={openCloseEmoji} style={{ cursor: isMatched ? 'pointer':'not-allowed' }} /> */}
              <textarea
                rows="2"
                value={textareaValue}
                onChange={(e) => setTextareaValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setEmojiVisibility(false)}
                autoComplete="off"
                disabled={!isMatched}
              />
              <button onClick={sendMessage} disabled={!isMatched}>
                Send
              </button>
            </CreatePost>
          </ChatControls>
        </ChatSection>
      </Container>
    </>
  );
};

export default TextChat;

const Container = styled.main`
  display: flex;
  align-items: center;
  height: 86vh;
  background-color: white;
  justify-content: space-around;

  @media only screen and (max-width: 600px) {
    height: 90vh; 
  }
`;

const ChatSection = styled.div`
  display: flex;
  height: auto;
  width: 80vw;
  border-radius: 5px;
  background: rgba(217, 217, 217, 0.5);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  align-items: center;
  flex-direction: column;

  @media only screen and (max-width: 600px) {
    width: 90vw;
  }
`;

const Head = styled.div`
  width: 90%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  `;
  
  const bounce = keyframes`
    0% {
      transform: rotate(0deg) translateX(10px) rotate(0deg);
    }
    100% {
      transform: rotate(360deg) translateX(10px) rotate(-360deg);
    }
  `;

const Info = styled.div`
  margin-left: -30px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;

  span {
    display: inline-block;
    animation: ${bounce} 1s infinite
  }

  @media only screen and (max-width: 600px) {
    font-size: 18px;
    margin-left: 1px;
  }
`;

const UserControls = styled.div`
  display: flex;
  gap: 15px;

  @media only screen and (max-width: 600px) {
    scale: 0.6;
  }
`;

const Chats = styled.div`
  display: flex;
  flex-direction: column;
  width: 95%;
  background: white;
  border-radius: 5px;
  padding: 10px;
  height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  gap: 1%;
`;

const ChatControls = styled.div`
  position: relative;
  width: 100%;
  padding: 10px 10px;
`;

const CreatePost = styled.div`
  display: flex;
  justify-content: space-around;
  background: transparent;

  img {
    height: 30px;
  }

  textarea {
    height: 60px;
    width: 70%;
    font-size: 20px;
    padding: 5px;
    vertical-align: middle;
  }

  button {
    background-color: #6559a2;
    padding: 20px;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    box-shadow: rgba(0, 0, 0, 0.2) -3px 3px 0px;

    &:disabled {
      cursor: not-allowed;
    }
  }
`;

const Message = styled.div`
  padding: 2vh 5vw;
  border-radius: ${({ className }) => {
    if (className === 'mine') {
      return '1.125rem 1.125rem 0 1.125rem';
    } else if (className === 'user') {
      return '1.125rem 1.125rem 1.125rem 0';
    } else {
      return '10px';
    }
  }};
  align-self: ${({ className }) => {
    if (className === 'mine') {
      return 'flex-end';
    } else if (className === 'user') {
      return 'flex-start';
    } else {
      return 'center';
    }
  }};
  color: ${({ className }) => {
    if (className === 'mine') {
      return 'white';
    } else {
      return 'black';
    }
  }};
  background: ${({ className }) => {
    if (className === 'mine') {
      return 'rgb(101, 89, 162, 0.9)';
    } else if (className === 'user') {
      return '#D9D9D9';
    } else if (className === 'system') {
      return 'none';
    }
  }};
  max-width: 75%;

  p {
    margin: 0;
    word-wrap: break-word;
  }
`;